/**
 * Resend Email Configuration (tenant-facing).
 *
 * Replaces the SendPulse tenant configuration UI. Uses the additive
 * /api/resend/* endpoints — fully isolated from the core SaaS Resend
 * pipeline and from legacy SendPulse infrastructure.
 *
 * Permissions:
 *   View: owner, admin, super_admin, manager, supervisor
 *   Edit: owner, admin, super_admin
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Mail,
  Eye,
  EyeOff,
  Send,
  Loader2,
  Info,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { resendAPI } from '../../lib/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ResendSettings({ user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const [config, setConfig] = useState({
    enabled: false,
    api_key_configured: false,
    api_key_masked: '',
    from_email: '',
    from_name: '',
    reply_to: '',
  });

  const [formData, setFormData] = useState({
    api_key: '',
    from_email: '',
    from_name: '',
    reply_to: '',
    enabled: false,
  });

  const [showApiKey, setShowApiKey] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const canEdit = ['owner', 'admin', 'super_admin'].includes(user?.role);
  const canView = ['owner', 'admin', 'super_admin', 'manager', 'supervisor'].includes(user?.role);

  useEffect(() => {
    if (canView) fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const { data } = await resendAPI.getConfig();
      setConfig(data);
      setFormData({
        api_key: '',
        from_email: data.from_email || '',
        from_name: data.from_name || '',
        reply_to: data.reply_to || '',
        enabled: data.enabled || false,
      });
    } catch (err) {
      // Empty/unconfigured tenants and stale-backend deploys should NOT toast.
      // Only surface an error on real server failure (5xx) or network failure.
      const status = err?.response?.status;
      const isUnconfigured =
        status === 404 || status === 204 || status === 400 || status === 403;
      if (isUnconfigured) {
        console.warn('Resend config endpoint returned', status, '— falling back to empty form');
      } else {
        console.error('Failed to fetch Resend config:', err);
        toast.error('Failed to load Resend configuration');
      }
      // In every error path, render the empty/default form so the user can still configure.
      setConfig({
        enabled: false,
        api_key_configured: false,
        api_key_masked: '',
        from_email: '',
        from_name: '',
        reply_to: '',
      });
      setFormData({
        api_key: '',
        from_email: '',
        from_name: '',
        reply_to: '',
        enabled: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) {
      toast.error('You do not have permission to edit Resend settings');
      return;
    }

    if (formData.enabled) {
      if (!config.api_key_configured && !formData.api_key) {
        toast.error('Resend API Key is required');
        return;
      }
      if (!formData.from_email || !formData.from_name) {
        toast.error('From Email and From Name are required');
        return;
      }
      if (!EMAIL_RE.test(formData.from_email)) {
        toast.error('Please enter a valid From Email address');
        return;
      }
      if (formData.reply_to && !EMAIL_RE.test(formData.reply_to)) {
        toast.error('Please enter a valid Reply-To email address');
        return;
      }
    }

    try {
      setSaving(true);
      const updates = {};
      if (formData.api_key) updates.api_key = formData.api_key.trim();
      if (formData.from_email !== config.from_email) updates.from_email = formData.from_email;
      if (formData.from_name !== config.from_name) updates.from_name = formData.from_name;
      if (formData.reply_to !== config.reply_to) updates.reply_to = formData.reply_to;
      if (formData.enabled !== config.enabled) updates.enabled = formData.enabled;

      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        return;
      }

      await resendAPI.updateConfig(updates);
      toast.success('Resend configuration saved');
      await fetchConfig();
      setFormData((prev) => ({ ...prev, api_key: '' }));
    } catch (err) {
      console.error('Failed to save Resend config:', err);
      toast.error(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!canEdit) {
      toast.error('You do not have permission to send test emails');
      return;
    }
    if (!testEmail) {
      toast.error('Please enter a recipient email address');
      return;
    }
    if (!EMAIL_RE.test(testEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    try {
      setSendingTest(true);
      await resendAPI.sendTestEmail(testEmail);
      toast.success(`Test email sent to ${testEmail}`);
      setTestEmail('');
    } catch (err) {
      console.error('Failed to send test email:', err);
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  if (!canView) {
    return (
      <Card data-settings-section="resend">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-orange-500" />
            Staff Email Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-500">
            You do not have permission to view Resend settings.
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card data-settings-section="resend">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-orange-500" />
            Staff Email Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-settings-section="resend" data-testid="resend-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-orange-500" />
          Staff Email Notifications
        </CardTitle>
        <div className="flex items-center justify-between mt-1 gap-3 flex-wrap">
          <p className="text-sm text-slate-500">
            Send transactional emails to your staff using your own Resend account.
          </p>
          <a
            href="https://getrestaff.co/help/resend-smtp"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-orange-600 hover:text-orange-700 hover:underline"
            data-testid="resend-setup-guide-link"
          >
            Setup Guide
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Setup help */}
        <div
          className="rounded-md border border-orange-200 bg-orange-50 p-4 text-sm text-slate-700"
          data-testid="resend-setup-help"
        >
          <div className="flex items-start gap-2 mb-2">
            <Info className="h-4 w-4 mt-0.5 text-orange-600 flex-shrink-0" />
            <p className="font-medium text-slate-900">How to set up Resend</p>
          </div>
          <ol className="ml-6 list-decimal space-y-1 text-slate-700">
            <li>
              Create an account at{' '}
              <a
                href="https://resend.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-600 hover:underline inline-flex items-center gap-1"
              >
                resend.com <ExternalLink className="h-3 w-3" />
              </a>
            </li>
            <li>Verify your sending domain in the Resend dashboard</li>
            <li>Generate an API key from <span className="font-mono text-xs">API Keys</span></li>
            <li>Paste the API key into ReStaff below</li>
            <li>Set your sender email and name</li>
          </ol>
          <div className="mt-3 rounded bg-white border border-slate-200 p-3 text-xs font-mono text-slate-600">
            <div>API Key: re_xxxxxxxxx</div>
            <div>From Email: hello@company.com</div>
            <div>From Name: Company Name</div>
          </div>
        </div>

        {/* Enabled toggle */}
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="resend-enabled" className="text-base font-medium">
              Enable Resend
            </Label>
            <p className="text-xs text-slate-500 mt-0.5">
              When enabled, ReStaff will send tenant emails via your Resend account.
            </p>
          </div>
          <Switch
            id="resend-enabled"
            checked={formData.enabled}
            onCheckedChange={(v) => setFormData((p) => ({ ...p, enabled: v }))}
            disabled={!canEdit}
            data-testid="resend-enabled-switch"
          />
        </div>

        {/* API key */}
        <div className="space-y-2">
          <Label htmlFor="resend-api-key">
            Resend API Key <span className="text-rose-600">*</span>
          </Label>
          <div className="relative">
            <Input
              id="resend-api-key"
              type={showApiKey ? 'text' : 'password'}
              placeholder={
                config.api_key_configured
                  ? `Configured (${config.api_key_masked}) — enter new key to replace`
                  : 're_xxxxxxxxx'
              }
              value={formData.api_key}
              onChange={(e) => setFormData((p) => ({ ...p, api_key: e.target.value }))}
              disabled={!canEdit}
              autoComplete="off"
              data-testid="resend-api-key-input"
            />
            <button
              type="button"
              onClick={() => setShowApiKey((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label={showApiKey ? 'Hide key' : 'Show key'}
              data-testid="resend-api-key-toggle"
            >
              {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-slate-500">
            Your key is encrypted before storage and never displayed in full.
          </p>
        </div>

        {/* From email + name */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="resend-from-email">
              From Email <span className="text-rose-600">*</span>
            </Label>
            <Input
              id="resend-from-email"
              type="email"
              placeholder="hello@company.com"
              value={formData.from_email}
              onChange={(e) => setFormData((p) => ({ ...p, from_email: e.target.value }))}
              disabled={!canEdit}
              data-testid="resend-from-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="resend-from-name">
              From Name <span className="text-rose-600">*</span>
            </Label>
            <Input
              id="resend-from-name"
              placeholder="Company Name"
              value={formData.from_name}
              onChange={(e) => setFormData((p) => ({ ...p, from_name: e.target.value }))}
              disabled={!canEdit}
              data-testid="resend-from-name-input"
            />
          </div>
        </div>

        {/* Reply-To */}
        <div className="space-y-2">
          <Label htmlFor="resend-reply-to">
            Reply-To Email <span className="text-slate-400 text-xs font-normal">(optional)</span>
          </Label>
          <Input
            id="resend-reply-to"
            type="email"
            placeholder="replies@company.com"
            value={formData.reply_to}
            onChange={(e) => setFormData((p) => ({ ...p, reply_to: e.target.value }))}
            disabled={!canEdit}
            data-testid="resend-reply-to-input"
          />
        </div>

        {/* Save */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={!canEdit || saving}
            data-testid="resend-save-btn"
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving</>
            ) : (
              'Save configuration'
            )}
          </Button>
        </div>

        {/* Test email */}
        <div className="border-t border-slate-200 pt-6 space-y-3">
          <div>
            <Label className="text-base font-medium">Send a test email</Label>
            <p className="text-xs text-slate-500 mt-0.5">
              Verifies your Resend credentials by sending one email via the Resend REST API.
            </p>
          </div>
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="you@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              disabled={!canEdit}
              data-testid="resend-test-email-input"
            />
            <Button
              onClick={handleSendTest}
              disabled={!canEdit || sendingTest || !config.enabled}
              variant="outline"
              data-testid="resend-test-email-btn"
            >
              {sendingTest ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Send test</>
              )}
            </Button>
          </div>
          {!config.enabled && (
            <p className="text-xs text-slate-400">
              Enable Resend and save your configuration to send a test email.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
